import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { logout, logoutAdmin } from "../../actions/auth";
import {
  LuBriefcase,
  LuCalendarCheck,
  LuCalendarClock,
  LuCalendarDays,
  LuCar,
  LuCarFront,
  LuChevronRight,
  LuFileText,
  LuIdCard,
  LuLayoutDashboard,
  LuLayoutTemplate,
  LuLogOut,
  LuMail,
  LuPackage,
  LuPlane,
  LuReceipt,
  LuSettings,
  LuShare2,
  LuShoppingCart,
  LuTags,
  LuUserCog,
  LuUsers,
  LuWarehouse,
} from "react-icons/lu";
import "./sidebar.css";

/**
 * Sidebar navigation for the admin dashboard.
 *
 * The screens are declared as data below and grouped by what an admin is doing
 * (operations / fleet / customers / catalogue / content / account) instead of
 * one flat list. `paths` holds every route the entry owns — its list, edit and
 * create screens — so the group stays highlighted while you work inside it.
 */

const NAV_GROUPS = [
  {
    id: "overview",
    items: [{ label: "Dashboard", icon: LuLayoutDashboard, to: "/admin/dashboard" }],
  },
  {
    id: "operations",
    label: "Operations",
    items: [
      {
        label: "Bookings",
        icon: LuCalendarCheck,
        to: "/admin/bookinglist",
        paths: ["/admin/bookinglist", "/admin/createbooking", "/admin/createbookingnew", "/admin/booking-edit"],
      },
      {
        label: "Quotations",
        icon: LuFileText,
        to: "/admin/quotations",
        flag: () => Boolean(process.env.REACT_APP_SHOW_QUOTATION),
      },
      {
        label: "Modification Requests",
        icon: LuCalendarClock,
        to: "/admin/modification-requests",
      },
      { label: "Abandoned Carts", icon: LuShoppingCart, to: "/admin/abandentcartlist" },
      { label: "Booking Calendar", icon: LuCalendarDays, to: "/admin/bookingcalendar" },
    ],
  },
  {
    id: "fleet",
    label: "Fleet",
    items: [
      { label: "Vehicles", icon: LuCar, to: "/admin/vehiclelist", paths: ["/admin/vehiclelist", "/admin/addvehicle"] },
      {
        label: "Vehicle Types",
        icon: LuCarFront,
        to: "/admin/vehicletypelist",
        paths: ["/admin/vehicletypelist", "/admin/addvehicletype"],
      },
      { label: "Drivers", icon: LuIdCard, to: "/admin/driverlist", paths: ["/admin/driverlist", "/admin/adddriver"] },
      { label: "Garages", icon: LuWarehouse, to: "/admin/garagelist", paths: ["/admin/garagelist", "/admin/addgarage"] },
    ],
  },
  {
    id: "customers",
    label: "Customers",
    items: [
      { label: "Users", icon: LuUsers, to: "/admin/userlist-individual" },
      { label: "B2B Partners", icon: LuBriefcase, to: "/admin/userlist-business" },
      {
        label: "Invoices",
        icon: LuReceipt,
        to: "/admin/invoices",
        flag: () => process.env.REACT_APP_INVOICE_MENU === "true",
      },
      {
        label: "Referrals",
        icon: LuShare2,
        to: "/admin/referrallist",
        paths: ["/admin/referrallist", "/admin/addreferral"],
      },
    ],
  },
  {
    id: "catalogue",
    label: "Catalogue",
    items: [
      {
        label: "Packages",
        icon: LuPackage,
        to: "/admin/newpackagelist",
        paths: ["/admin/newpackagelist", "/admin/packagelist", "/admin/addpackage"],
      },
      { label: "Airports", icon: LuPlane, to: "/admin/airportlist", paths: ["/admin/airportlist", "/admin/addairport"] },
      {
        label: "Categories",
        icon: LuTags,
        to: "/admin/categorylist",
        paths: ["/admin/categorylist", "/admin/addcategory"],
      },
    ],
  },
];

// The CMS pages get their own collapsible entry — 24 links flattened into the
// main list buried everything below them.
const CMS_PAGES = [
  { label: "Index", to: "/admin/cms/cmsindex" },
  { label: "Airport Transfer", to: "/admin/cms/cms-airport-transfer" },
  { label: "Haneda Airport Transfer", to: "/admin/cms/cms-haneda-airport-transfer" },
  { label: "Narita Airport Transfer", to: "/admin/cms/cms-narita-airport-transfer" },
  { label: "Haneda Airport", to: "/admin/cms/cms-haneda-airport" },
  { label: "Narita Airport", to: "/admin/cms/cms-narita-airport" },
  { label: "Tokyo Station", to: "/admin/cms/cms-tokyo-station" },
  { label: "Weddings & Anniversaries", to: "/admin/cms/cms-weddings-anniversaries" },
  { label: "Private Tour", to: "/admin/cms/cms-private-tour" },
  { label: "Executive Transfer", to: "/admin/cms/cms-executive-transfer" },
  { label: "Corporate", to: "/admin/cms/cms-corporate" },
  { label: "Careers", to: "/admin/cms/cms-careers" },
  { label: "Fleet", to: "/admin/cms/cms-fleet" },
  { label: "Alphard", to: "/admin/cms/cms-alphard" },
  { label: "Granace", to: "/admin/cms/cms-granace" },
  { label: "HiAce Grand Cabin", to: "/admin/cms/cms-hiace-grand-cabin" },
  { label: "HiAce Luxury Cabin", to: "/admin/cms/cms-hiace-luxury-cabin" },
  { label: "Pricing", to: "/admin/cms/cms-pricing" },
  { label: "Maps", to: "/admin/cms/cms-maps" },
  { label: "Cart", to: "/admin/cms/cms-cart" },
  { label: "Checkout", to: "/admin/cms/cms-checkout" },
  { label: "Contact", to: "/admin/cms/cms-contact" },
  { label: "Terms", to: "/admin/cms/cms-terms" },
  { label: "Cancellation Policy", to: "/admin/cms/cms-cancellation-policy" },
];

const ACCOUNT_ITEMS = [
  {
    label: "Admin Users",
    icon: LuUserCog,
    to: "/admin/userlist-admin",
    paths: ["/admin/userlist-admin", "/admin/adduser", "/admin/useredit"],
  },
  { label: "Settings", icon: LuSettings, to: "/admin/settings" },
];

const isItemActive = (item, pathname) =>
  (item.paths || [item.to]).some((path) => pathname === path || pathname.startsWith(`${path}/`));

const NavLink = ({ item, pathname }) => {
  const Icon = item.icon;

  return (
    <li>
      <Link
        to={item.to}
        className={`admin-sidebar-link${isItemActive(item, pathname) ? " is-active" : ""}`}
      >
        <Icon className="admin-sidebar-icon" size={17} />
        <span className="admin-sidebar-label">{item.label}</span>
      </Link>
    </li>
  );
};

const NavGroup = ({ group, pathname }) => {
  const items = group.items.filter((item) => !item.flag || item.flag());

  if (items.length === 0) return null;

  return (
    <div className="admin-sidebar-group">
      {group.label ? <div className="admin-sidebar-group-label">{group.label}</div> : null}
      <ul className="admin-sidebar-list">
        {items.map((item) => (
          <NavLink key={item.to} item={item} pathname={pathname} />
        ))}
      </ul>
    </div>
  );
};

const Navbar = ({ logoutAdmin, visible }) => {
  const location = useLocation();
  const { pathname } = location;
  const isCmsRoute = pathname.startsWith("/admin/cms");
  const [cmsOpen, setCmsOpen] = useState(isCmsRoute);
  const scrollRef = useRef(null);

  // The list is taller than the viewport, so bring whatever is current into
  // view rather than always opening at the top.
  useEffect(() => {
    const active = scrollRef.current?.querySelector(".admin-sidebar-link.is-active");
    active?.scrollIntoView({ block: "nearest" });
  }, [pathname, cmsOpen]);

  return (
    <nav
      id="sidebarMenu"
      className={`admin-sidebar d-md-block sidebar collapse tw:w-[250px] ${visible ? "" : " d-none"}`}
      style={{ transition: "left 0.3s", left: visible ? 0 : -250 }}
      aria-label="Admin navigation"
    >
      <div className="admin-sidebar-scroll" ref={scrollRef}>
        {NAV_GROUPS.map((group) => (
          <NavGroup key={group.id} group={group} pathname={pathname} />
        ))}

        <div className="admin-sidebar-group">
          <div className="admin-sidebar-group-label">Content</div>
          <ul className="admin-sidebar-list">
            <li>
              <button
                type="button"
                onClick={() => setCmsOpen((prev) => !prev)}
                aria-expanded={cmsOpen}
                className={`admin-sidebar-link${isCmsRoute && !cmsOpen ? " is-active" : ""}`}
              >
                <LuLayoutTemplate className="admin-sidebar-icon" size={17} />
                <span className="admin-sidebar-label">CMS Pages</span>
                <LuChevronRight className={`admin-sidebar-caret${cmsOpen ? " is-open" : ""}`} size={14} />
              </button>

              {cmsOpen ? (
                <ul className="admin-sidebar-sublist">
                  {CMS_PAGES.map((page) => (
                    <li key={page.to}>
                      <Link
                        to={page.to}
                        className={`admin-sidebar-link admin-sidebar-sublink${pathname === page.to ? " is-active" : ""}`}
                      >
                        <span className="admin-sidebar-label">{page.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>

            <NavLink
              item={{
                label: "Email Templates",
                icon: LuMail,
                to: "/admin/templatelist",
                paths: ["/admin/templatelist", "/admin/addemailtemplate"],
              }}
              pathname={pathname}
            />
          </ul>
        </div>

        <div className="admin-sidebar-group">
          <div className="admin-sidebar-group-label">Account</div>
          <ul className="admin-sidebar-list">
            {ACCOUNT_ITEMS.map((item) => (
              <NavLink key={item.to} item={item} pathname={pathname} />
            ))}
            <li>
              <button
                type="button"
                onClick={logoutAdmin}
                className="admin-sidebar-link admin-sidebar-signout"
              >
                <LuLogOut className="admin-sidebar-icon" size={17} />
                <span className="admin-sidebar-label">Sign Out</span>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

Navbar.propTypes = {
  auth: PropTypes.object.isRequired,
  logout: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { logout, logoutAdmin })(Navbar);
